import { PageHeader } from "../../bits/PageHeader/PageHeader";
import {
    ClientStatus,
    DateFormats,
    IClient,
    ReportDuration,
} from "intake_shared/src/types";
import * as React from "react";
import store from "../../../../store";
import { getClientsWithBooking } from "../../../../utils/booked_clients";
import moment from "moment";
import { Link } from "react-router-dom";
import Table from "../../bits/Table/Table";
import EmptyListState from "../../bits/EmptyListState/EmptyListState";
import { displayPhoneNumber } from "../../../../utils/utils";
import { getClientRowStylesForInquiryMethod } from "../../../../constants";

export default function BookedClientsList() {
    const [completedClients, setCompletedClients] = React.useState([] as IClient[]);

    React.useEffect(() => {
        const getCompletedClients = async (duration: ReportDuration = "day") => {
            const todayStatuses =
                ((await store.getReportData(duration)) || {}).status || {};
            const completedClientIds = Object.keys(todayStatuses).filter(
                (s) => todayStatuses[s] === ClientStatus.APPT_CONFIRMED
            );
            if (completedClientIds.length) {
                const clients = await store.fetchClientsByIds(completedClientIds);
                const clientsWithBookings = getClientsWithBooking(clients);
                if (clientsWithBookings.length > 0) {
                    setCompletedClients(clientsWithBookings);
                }
            }
        };
        getCompletedClients();
    }, []);

    const columns = React.useMemo(() => {
        return [
            {
                Header: "Booked at",
                accessor: (row: IClient) => {
                    return (
                        <p>
                            {`${moment(
                                (
                                    row?.status_history[
                                        ClientStatus.APPT_CONFIRMED
                                    ] as any
                                )?.toDate()
                            ).format(DateFormats.DAY_HOUR_MIN_FORMAT)}`}
                        </p>
                    );
                },
            },
            {
                Header: "Email",
                id: "email",
                accessor: (row: IClient) => {
                    return <div>{row.email || "--"}</div>;
                },
            },
            {
                Header: "Phone no",
                id: "phone_no",
                accessor: (row: IClient) => {
                    return (
                        <div>
                            {row.phone_no ? displayPhoneNumber(row.phone_no) : "--"}
                        </div>
                    );
                },
            },
            {
                Header: "Last name",
                id: "last_name",
                accessor: (row: IClient) => {
                    return <div>{row.last_name || "--"}</div>;
                },
            },
            {
                Header: "First name",
                id: "first_name",
                accessor: (row: IClient) => {
                    return <div>{row.first_name || "--"}</div>;
                },
            },
            {
                Header: "Pronoun",
                id: "pronouns",
                accessor: (row: IClient) => {
                    return <div>{row.pronouns || "--"}</div>;
                },
            },
            {
                Header: "Inquiry method",
                id: "inquiry_method",
                accessor: (row: IClient) => {
                    return <div>{row.inquiry_method || "--"}</div>;
                },
            },
            {
                Header: "Appointments",
                accessor: (row: IClient) => (
                    <div>
                        {row.confirmed_bookings.map((b) => (
                            <p>
                                {b.clinician_display_name} - {b.location} -{" "}
                                {moment(b.start).format(DateFormats.DAY_HOUR_FORMAT)}{" "}
                                - Starting{" "}
                                {moment(b.starting_date).format(
                                    DateFormats.DAY_MONTH_FORMAT
                                )}
                            </p>
                        ))}
                    </div>
                ),
            },
            {
                Header: "",
                id: "view-booked-client",
                accessor: (row: IClient) => (
                    <Link
                        to={{
                            pathname: `/v2/client-full-view`,
                            state: { client: row, enableOtherStatuses: true },
                        }}
                    >
                        View
                    </Link>
                ),
            },
        ];
    }, []);

    return (
        <div className="v2-page">
            <PageHeader
                title="Booked Clients"
                meta={`${completedClients.length} today · ${moment().format(
                    DateFormats.DATE_FORMAT
                )}`}
            />

            {completedClients.length > 0 ? (
                <Table
                    columns={columns}
                    data={completedClients}
                    allowSelect={false}
                    stickyLastColumn
                    handleRowStyle={(row) =>
                        getClientRowStylesForInquiryMethod(
                            row.original.inquiry_method
                        )
                    }
                />
            ) : (
                <EmptyListState
                    emptyMessage="No booked clients today"
                    stateHelperText="No appointments have been recorded for today."
                />
            )}
        </div>
    );
}
